import { useEffect, useState } from 'react';

import { initNorskGjeldService } from '../../api/initNorskGjeld';
import { callbackUrl } from '../../api/util';
import Description from '../../components/Description/Description';
import { ErrorMessage } from '../../components/ErrorMessage';
import PagesFooter from '../../components/PagesFooter/PagesFooter';
import PagesHeader from '../../components/PagesHeader/PagesHeader';
import Title from '../../components/Title/Title';
import { usePagesContext } from '../../context/PagesContext';
import { ConsumerLoanTargetPath } from '../../domain/types';
import { useAppSelector } from '../../hooks';
import useArnFromPath from '../../hooks/useArnFromPath';
import { useConsentIdParams } from '../../hooks/useConsentIdParams';
import useDefaultPagesAction from '../../hooks/useDefaultPagesAction';
import useSaveDraftConsent from '../../hooks/useSaveDraftConsent';
import useSetBannerType from '../../hooks/useSetBannerType';
import useSettings from '../../hooks/useSettings';
import useTranslation from '../../hooks/useTranslation';
import { selectFieldStateByName } from '../../store/applicationform/applicationFormSelectors';
import { WizardPageAction } from '../../store/wizard/wizard';

const PARAM_CONSENT_STATE = 'consent_state';
const CONSENT_STATE_ERROR = 'error';
const CONSENT_STATE_SUCCESS = 'success';
const CO_APPLICANT_GOVERNMENT_ID = 'coapplicantGovernmentId';
const CO_APPLICANT_NORSK_GJELD_REFERENCE_ID = 'coapplicantGjeldsregisteretId';

const CoApplicantRequestNorskGjeldConsentPage = () => {
    const { countryCode, languageCode } = useSettings();
    const translate = useTranslation();
    const { currentPage, pageAction } = usePagesContext();
    const defaultPagesAction = useDefaultPagesAction();
    useSetBannerType('bannerOne');
    const { apiKey, proxyBaseUrl } = useSettings();
    const [errorOccurred, setErrorOccurred] = useState<boolean>(false);
    useConsentIdParams('coapplicantGovernmentId');
    const applicationReferenceNumber = useArnFromPath();
    const { authenticationData } = useAppSelector((state) => state.authentication);
    const coapplicantGovernmentId = useAppSelector(selectFieldStateByName)(CO_APPLICANT_GOVERNMENT_ID)?.value;
    const coapplicantNorskGjeldReferenceIdFieldState = useAppSelector(selectFieldStateByName)(
        CO_APPLICANT_NORSK_GJELD_REFERENCE_ID,
    );
    const saveDraftConsent = useSaveDraftConsent();

    useEffect(() => {
        let isMounted = true;

        if ((pageAction === 'back' || pageAction === 'next') && coapplicantNorskGjeldReferenceIdFieldState?.value) {
            const saveConsent = async () => {
                try {
                    if (coapplicantNorskGjeldReferenceIdFieldState?.value) {
                        await saveDraftConsent(
                            coapplicantNorskGjeldReferenceIdFieldState.value.toString(),
                            'NORSK_GJELDSREGISTER',
                        );
                        if (isMounted) {
                            defaultPagesAction(pageAction);
                        }
                    }
                } catch (error) {
                    if (isMounted) {
                        setErrorOccurred(true);
                    }
                }
            };
            saveConsent();
        }

        return () => {
            isMounted = false;
        };
    }, [pageAction, coapplicantNorskGjeldReferenceIdFieldState?.value, saveDraftConsent, defaultPagesAction]);

    const onAction = async (action: WizardPageAction) => {
        if (action === 'consent') {
            try {
                const successUrl = `${callbackUrl(
                    ConsumerLoanTargetPath.coapplicantDraft,
                    languageCode,
                    countryCode,
                    applicationReferenceNumber!,
                )}?${PARAM_CONSENT_STATE}=${CONSENT_STATE_SUCCESS}`;
                const failureUrl = `${callbackUrl(
                    ConsumerLoanTargetPath.coapplicantDraft,
                    languageCode,
                    countryCode,
                    applicationReferenceNumber!,
                )}?${PARAM_CONSENT_STATE}=${CONSENT_STATE_ERROR}`;

                const response = await initNorskGjeldService(
                    proxyBaseUrl,
                    apiKey,
                    coapplicantGovernmentId as string,
                    successUrl,
                    failureUrl,
                    authenticationData,
                );
                window.location.replace(response.consentUrl);
            } catch (error) {
                setErrorOccurred(true);
                throw error;
            }
            return false;
        }

        return defaultPagesAction(action);
    };

    return (
        <>
            <PagesHeader
                actions={['back']}
                onAction={onAction}
                currentPage={currentPage}
                currentSection={1}
                numberOfSections={3}
                numberOfPagesInSection={1}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.norsk-gjeld')}
            />
            <>
                <Title text={translate('page.request-norsk-gjeld.title')} />
                <Description text={translate('page.request-norsk-gjeld.description')} />
            </>
            {errorOccurred && (
                <ErrorMessage
                    title={translate(`page.error.title`)}
                    description={translate(`page.authentication.submit.log.in.error.description`)}
                />
            )}
            {coapplicantNorskGjeldReferenceIdFieldState?.hasError && (
                <ErrorMessage
                    title={translate('page.norsk-gjeld.consolidations.error.title')}
                    description={translate('page.norsk-gjeld.consolidations.error.description')}
                />
            )}
            <PagesFooter
                actions={['consent']}
                onAction={onAction}
                currentSection={1}
                numberOfSections={3}
                numberOfPagesInSection={1}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.norsk-gjeld')}
            />
        </>
    );
};

export default CoApplicantRequestNorskGjeldConsentPage;
